if (!customElements.get("anchor-nav")) {
  customElements.define(
    "anchor-nav",
    class AnchorNav extends HTMLElement {
      constructor() {
        super();

        this.anchors = [];
        this.updateFrame = null;
        this.resizeObserver = null;

        this.requestUpdate = this.requestUpdate.bind(this);
        this.refresh = this.refresh.bind(this);

        // Listen for anchor clicks and scroll to the corresponding position
        this.addEventListener("click", (event) => {
          const item = event.target.closest(".anchor-item");
          if (!item || !this.contains(item)) return;

          event.preventDefault();

          // Scroll to anchor position
          const target = document.getElementById(item.dataset.targetId);
          if (target) {
            webvista.scrollToElementWithOffset(target, this.scrollOffset);
          }
        });
      }

      connectedCallback() {
        this.refresh();

        window.addEventListener("scroll", this.requestUpdate, {
          passive: true,
        });
        window.addEventListener("resize", this.requestUpdate);

        // Section markup is re-rendered in the theme editor, which leaves the
        // resolved targets detached from the document. Resolve them again.
        document.addEventListener("shopify:section:load", this.refresh);
        document.addEventListener("shopify:section:unload", this.refresh);
        document.addEventListener("shopify:section:reorder", this.refresh);

        // The underline is measured from the rendered items, so it has to be
        // recalculated once webfonts land and whenever the nav is resized.
        if (document.fonts) document.fonts.ready.then(this.requestUpdate);
        if ("ResizeObserver" in window) {
          this.resizeObserver = new ResizeObserver(this.requestUpdate);
          this.resizeObserver.observe(this);
        }
      }

      disconnectedCallback() {
        window.removeEventListener("scroll", this.requestUpdate);
        window.removeEventListener("resize", this.requestUpdate);
        document.removeEventListener("shopify:section:load", this.refresh);
        document.removeEventListener("shopify:section:unload", this.refresh);
        document.removeEventListener("shopify:section:reorder", this.refresh);

        if (this.resizeObserver) this.resizeObserver.disconnect();
        if (this.updateFrame) cancelAnimationFrame(this.updateFrame);
      }

      /**
       * Distance from the top of the viewport at which a target starts counting
       * as the current one. Kept in sync with the offset used to scroll to an
       * anchor, so a clicked item stays highlighted once the scroll lands.
       * @returns {number}
       */
      get scrollOffset() {
        const offset = parseFloat(this.dataset.scrollOffset);
        return Number.isNaN(offset) ? window.innerHeight * 0.3 : offset;
      }

      /**
       * Resolves the anchor targets again and re-runs the scroll spy. Safe to
       * call at any point: targets are looked up fresh on every pass.
       * @returns {void}
       */
      refresh() {
        this.getAnchors();
        this.update();
      }

      /**
       * Collects all elements with the class "anchor-item" within the current context,
       * maps them to objects containing the element and its corresponding target element
       * (identified by the data-target-id attribute), and filters out anchors without a valid target.
       *
       * Anchors are sorted by the position of their target in the document, so
       * the order of the navigation items does not have to match the order the
       * sections are laid out in.
       *
       * @returns {void}
       */
      getAnchors() {
        this.anchors = Array.from(this.querySelectorAll(".anchor-item"))
          .map((item) => {
            return {
              element: item,
              target: this.resolveTarget(item.dataset.targetId),
            };
          })
          .filter((anchor) => anchor.target !== null)
          .sort((a, b) =>
            a.target.compareDocumentPosition(b.target) &
            Node.DOCUMENT_POSITION_FOLLOWING
              ? -1
              : 1,
          );
      }

      /**
       * Finds the section an anchor points at.
       *
       * Anchor ids are stored in the template as a full section id, e.g.
       * `shopify-section-template--19239695679572__related-products`. The
       * `template--<number>` part is specific to one copy of the theme, so it
       * stops matching as soon as the theme is duplicated, published as a new
       * copy, or served by `shopify theme dev` -- and every anchor silently
       * resolves to nothing. Fall back to the section key at the end of the id,
       * which stays the same across all of those.
       *
       * @param {string} targetId
       * @returns {Element|null}
       */
      resolveTarget(targetId) {
        if (!targetId) return null;

        const direct = document.getElementById(targetId);
        if (direct) return direct;

        const key = targetId.split("__").pop();
        if (!key) return null;

        const suffix = `__${key}`;
        const section = Array.from(
          document.querySelectorAll('[id^="shopify-section-"]'),
        ).find((element) => element.id.endsWith(suffix));

        // `shopify-section-<key>` covers sections that carry no prefix at all,
        // and a bare `<key>` covers custom anchors set on the target section.
        return (
          section ||
          document.getElementById(`shopify-section-${key}`) ||
          document.getElementById(key)
        );
      }

      /**
       * Queues an update on the next animation frame, so a burst of scroll
       * events only costs one pass over the anchors.
       * @returns {void}
       */
      requestUpdate() {
        if (this.updateFrame) return;

        this.updateFrame = requestAnimationFrame(() => {
          this.updateFrame = null;
          this.update();
        });
      }

      /**
       * Works out which target the reader is currently on and highlights the
       * matching item: the last target whose top edge has passed the reading
       * line. Falls back to the first anchor above the first section, and to
       * the last one at the end of the page, where a short final section may
       * never reach the line.
       * @returns {void}
       */
      update() {
        if (this.anchors.length <= 0) return;

        const line = this.scrollOffset;
        const atBottom =
          window.scrollY + window.innerHeight >=
          document.documentElement.scrollHeight - 2;

        let active = this.anchors[0];

        if (atBottom) {
          active = this.anchors[this.anchors.length - 1];
        } else {
          this.anchors.forEach((anchor) => {
            if (anchor.target.getBoundingClientRect().top <= line) {
              active = anchor;
            }
          });
        }

        this.setAnchorActive(active.element);
      }

      /**
       * Set anchor active
       * @param {Element} item
       * @returns
       */
      setAnchorActive(item) {
        if (!item) return;

        const changed = !item.classList.contains("active");

        if (changed) {
          this.querySelector(".active")?.classList.remove("active");
          item.classList.add("active");
        }

        // Set underline position
        this.setLinePosition(item);

        if (!changed) return;

        // Send a custom "item-active" event
        // Useful for scrollable content views to capture and move the item into viewport
        this.dispatchEvent(
          new CustomEvent("item-active", {
            bubbles: true,
            detail: {
              element: item,
            },
          }),
        );
      }

      /**
       * Set underline position
       * @param {Element} item
       * @returns
       */
      setLinePosition(item) {
        if (!item) return;

        const wrapperRect = this.getBoundingClientRect();

        // Nothing to measure while the nav is not laid out (hidden tab, section
        // still rendering); a later update will pick it up.
        if (wrapperRect.width <= 0) return;

        const itemRect = item.getBoundingClientRect();

        const left = itemRect.left - wrapperRect.left;
        const right = wrapperRect.right - itemRect.right;

        this.style.setProperty("--item-left", `${left}px`);
        this.style.setProperty("--item-right", `${right}px`);
      }
    },
  );
}
